"use client";

import { logDiagnostic } from "@/lib/silent-diagnostic";
import { Solana, EVM, config, createConfig, ChainType } from "@lifi/sdk";
import { useTokenStore } from "@/lib/zustand";
import type { SignerWalletAdapter } from "@solana/wallet-adapter-base";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { useWallet as useExternalSolanaWallet } from "@solana/wallet-adapter-react";
import { useEffect, useRef } from "react";
import { createWalletClient, custom, type Chain } from "viem";
import { evmChains } from "@/app/providers/para-provider";
import {
  useAccount,
  useWallet as useParaWallet,
  useClient,
} from "@getpara/react-sdk";
import { Connection, PublicKey, Transaction } from "@solana/web3.js";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { getWagmiConfig } from "@getpara/evm-wallet-connectors";
import { getSolanaRpcUrl } from "@/lib/rpc";
import { switchChain as switchChainAction, getWalletClient as getWalletClientAction } from "@wagmi/core";

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://forgeswap.fun";

createConfig({
  integrator: process.env.NEXT_PUBLIC_LIFI_ID || "prevail_swap",
});

// Shared ref to prevent setProviders from running during active swaps
// This prevents re-renders from balance refreshes resetting LiFi's provider state mid-swap
export const swapInProgressRef = { current: false };

/**
 * Wrap a wallet client in a Proxy to observe sendTransaction calls.
 * Only intercepts sendTransaction — all other methods pass through transparently.
 */
function wrapWalletClientWithDiagnostics<T extends object>(
  client: T,
  method: string
): T {
  return new Proxy(client, {
    get(target, prop, receiver) {
      if (prop === "sendTransaction") {
        const originalFn = Reflect.get(target, prop, receiver);
        return async (...args: unknown[]) => {
          const startTime = Date.now();
          logDiagnostic({ event: "sendTransaction-start", method });
          try {
            const result = await (originalFn as Function).apply(target, args);
            const elapsedMs = Date.now() - startTime;
            logDiagnostic({
              event: "sendTransaction-done",
              method,
              elapsedMs,
              hasResult: !!result,
              resultType: typeof result,
            });
            return result;
          } catch (e) {
            const elapsedMs = Date.now() - startTime;
            logDiagnostic({
              event: "sendTransaction-error",
              method,
              elapsedMs,
              error: String(e).slice(0, 150),
            });
            throw e;
          }
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as T;
}

export const SDKProviders = () => {
  const { wallet: externalSolanaWallet } = useExternalSolanaWallet();
  useAccount(); // Keep hook call for potential side effects
  const { data: paraWallet } = useParaWallet();
  const paraClient = useClient();
  const { isExternalEvm, walletType, address: paraAddress } = useParaAccount();
  const { fromChain } = useTokenStore();

  // Store current values in refs so callbacks always read fresh values
  const stateRef = useRef({
    walletType,
    isExternalEvm,
    paraWallet,
    paraAddress,
    externalSolanaWallet,
    paraClient,
    fromChain,
  });

  // Keep refs in sync with current values
  stateRef.current = {
    walletType,
    isExternalEvm,
    paraWallet,
    paraAddress,
    externalSolanaWallet,
    paraClient,
    fromChain,
  };

  // Configure SDK providers - runs on every state change to ensure LiFi has fresh providers
  // Skip if a swap is in progress to prevent resetting provider state mid-execution
  useEffect(() => {
    if (swapInProgressRef.current) {
      return;
    }

    const evmProvider = EVM({
      getWalletClient: async () => {
        const { walletType, isExternalEvm, fromChain } = stateRef.current;
        // Use fromChain.chainType (swap intent) instead of walletType (racing Para SDK state).
        // walletType updates asynchronously after fromChain changes, causing wrong code path
        // when user initiates swap before Para SDK catches up. Same pattern as switchChain below.
        const isEvmSwap = fromChain?.chainType === ChainType.EVM;
        logDiagnostic({ event: "walletclient-requested", walletType, isExternalEvm, fromChainType: fromChain?.chainType, isEvmSwap });

        // PRIORITY 1: Para internal wallet (email/social login) - use wagmi core action for fresh client
        // This is LiFi's recommended pattern to avoid stale closure issues
        if (isEvmSwap && !isExternalEvm) {
          logDiagnostic({ event: "walletclient-trying", method: "para-internal" });
          try {
            const freshClient = await getWalletClientAction(getWagmiConfig());
            if (freshClient?.account) {
              logDiagnostic({ event: "walletclient-returned", method: "para-internal" });
              return wrapWalletClientWithDiagnostics(freshClient, "para-internal");
            }
          } catch (e) {
            logDiagnostic({ event: "walletclient-error-suppressed", method: "para-internal", error: String(e).slice(0, 150) });
            // Fall through to external wallet check
          }
        }

        // PRIORITY 2: External EVM wallet (MetaMask, Phantom, etc.)
        if (isExternalEvm && typeof window !== "undefined" && window.ethereum) {
          logDiagnostic({ event: "walletclient-trying", method: "external-evm" });
          try {
            const accounts = await window.ethereum.request({
              method: "eth_accounts",
            });
            if (accounts?.[0]) {
              const chain: Chain | undefined = evmChains.find(c => c.id === fromChain?.id);
              logDiagnostic({ event: "walletclient-chain", method: "external-evm", chainId: chain?.id ?? null });
              const walletClient = createWalletClient({
                chain,
                transport: custom(window.ethereum),
                account: accounts[0] as `0x${string}`,
              });
              logDiagnostic({ event: "walletclient-returned", method: "external-evm" });
              return wrapWalletClientWithDiagnostics(walletClient, "external-evm");
            }
          } catch (e) {
            logDiagnostic({ event: "walletclient-error-suppressed", method: "external-evm", error: String(e).slice(0, 150) });
            // Fall through to fallback
          }
        }

        // Fallback: Try wagmi core action regardless of wallet type
        logDiagnostic({ event: "walletclient-trying", method: "fallback" });
        try {
          const freshClient = await getWalletClientAction(getWagmiConfig());
          if (freshClient?.account) {
            logDiagnostic({ event: "walletclient-returned", method: "fallback" });
            return wrapWalletClientWithDiagnostics(freshClient, "fallback");
          }
        } catch (e) {
          logDiagnostic({ event: "walletclient-error-suppressed", method: "fallback", error: String(e).slice(0, 150) });
          // No wallet available
        }

        logDiagnostic({ event: "walletclient-all-failed" });
        throw new Error("No EVM wallet connected");
      },
      switchChain: async (chainId: number) => {
        const { isExternalEvm } = stateRef.current;

        // Decision based solely on isExternalEvm, not walletType
        // walletType can be 'SOLANA' during brief transition to EVM mode, causing fallthrough to Phantom

        // For external EVM wallets (MetaMask, Phantom EVM, etc.) - use window.ethereum
        if (isExternalEvm && typeof window !== "undefined" && window.ethereum) {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${chainId.toString(16)}` }],
          });

          const accounts = await window.ethereum.request({
            method: "eth_accounts",
          });
          if (!accounts || accounts.length === 0) {
            throw new Error("No EVM wallet connected");
          }

          const chain: Chain | undefined = evmChains.find(c => c.id === chainId);
          logDiagnostic({ event: "switchchain-chain", method: "external-evm", chainId: chain?.id ?? null });
          const walletClient = createWalletClient({
            chain,
            transport: custom(window.ethereum),
            account: accounts[0] as `0x${string}`,
          });
          return walletClient;
        }

        // For Para internal wallets (email/social login) - use Para's wagmi config
        // This runs when isExternalEvm is false, regardless of current walletType
        // This prevents Phantom from intercepting chain switch requests meant for Para
        const paraWagmiConfig = getWagmiConfig();
        await switchChainAction(paraWagmiConfig, { chainId });
        const freshWalletClient = await getWalletClientAction(paraWagmiConfig, { chainId });
        return freshWalletClient;
      },
    });

    const solanaProvider = Solana({
      async getWalletAdapter() {
        const { paraWallet, externalSolanaWallet } = stateRef.current;

        // If Para SOLANA internal wallet is active, provide a custom adapter backed by Para signer
        if (!paraWallet?.isExternal && paraWallet?.address) {
          const publicKey = new PublicKey(paraWallet.address);
          const connection = new Connection(
            getSolanaRpcUrl()
          );

          const adapter: SignerWalletAdapter = {
            name: "Para",
            url: BASE_URL,
            icon: "",
            readyState: WalletReadyState.Installed,
            connecting: false,
            connected: true,
            supportedTransactionVersions: null,
            publicKey,
            async connect() {
              return;
            },
            async disconnect() {
              return;
            },
            async sendTransaction(
              transaction: Transaction,
              _conn?: Connection
            ): Promise<string> {
              if (!(window as any).solana) {
                throw new Error(
                  "Solana wallet not found. Please install a compatible wallet."
                );
              }
              const signedTransaction = await (
                window as any
              ).solana.signTransaction(transaction);
              const signature = await connection.sendRawTransaction(
                signedTransaction.serialize()
              );
              return signature;
            },
            async signTransaction(
              transaction: Transaction
            ): Promise<Transaction> {
              // Para signer API focuses on send; return transaction unchanged
              return transaction;
            },
            async signAllTransactions(
              transactions: Transaction[]
            ): Promise<Transaction[]> {
              return transactions;
            },
            on() {
              // no-op
            },
            off() {
              // no-op
            },
          } as unknown as SignerWalletAdapter;

          return adapter;
        }

        // Fallback to external wallet-adapter (e.g., Phantom)
        return externalSolanaWallet?.adapter as SignerWalletAdapter;
      },
    });

    config.setProviders([evmProvider, solanaProvider]);
  }, [
    externalSolanaWallet?.adapter,
    paraWallet?.type,
    paraWallet?.address,
    paraClient,
    isExternalEvm,
    walletType,
    paraAddress,
    fromChain?.chainType,
  ]);

  return null;
};
